// Principal from Cognito JWT claims. The claim names are the contract between the identity
// context (which configures the pool and the pre-token-generation hook) and everyone else:
//
//   sub                   Cognito subject
//   custom:tenantId       tenant this token acts in (`platform` for control-plane staff)
//   custom:tier           pool | silo
//   cognito:groups        roles, as an array or a comma-separated string
//   custom:officeCode     optional
//   custom:agentCode      optional
//   custom:grants         optional; JSON array or comma-separated `office#X` / `tenant#Y`
//   custom:memberships    optional; JSON array of { tenantId, roles, officeCode?, agentCode?, grants? }
//
// Unknown roles and malformed grants are dropped, never guessed. A missing tenant yields a
// principal that fails every tenant-scoped decision with `tenant-missing`.
import { z } from 'zod';
import { GRANT_PATTERN, membershipSchema, toPrincipal, } from '../principal.js';
import { isRoleName, isTierName } from '../roles.js';
export class ClaimsError extends Error {
    name = 'ClaimsError';
    constructor(detail) {
        super(`Cannot build a principal from claims: ${detail}`);
    }
}
function listClaim(value) {
    if (Array.isArray(value))
        return value.filter((v) => typeof v === 'string');
    if (typeof value !== 'string')
        return [];
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
        try {
            return listClaim(JSON.parse(trimmed));
        }
        catch {
            return [];
        }
    }
    return trimmed
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}
function stringClaim(value) {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}
export function rolesFromClaim(value) {
    return listClaim(value).filter(isRoleName);
}
export function grantsFromClaim(value) {
    return listClaim(value).filter((g) => GRANT_PATTERN.test(g));
}
const membershipsClaimSchema = z.array(membershipSchema);
export function membershipsFromClaim(value) {
    let raw = value;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        }
        catch {
            return [];
        }
    }
    const parsed = membershipsClaimSchema.safeParse(raw);
    return parsed.success ? parsed.data : [];
}
export function tierFromClaim(value) {
    return isTierName(value) ? value : 'pool';
}
export function principalFromJwtClaims(claims) {
    const sub = stringClaim(claims['sub']);
    if (!sub)
        throw new ClaimsError('missing sub');
    const tenantId = stringClaim(claims['custom:tenantId']);
    const officeCode = stringClaim(claims['custom:officeCode']);
    const agentCode = stringClaim(claims['custom:agentCode']);
    return toPrincipal({
        sub,
        ...(tenantId !== undefined ? { tenantId } : {}),
        tier: tierFromClaim(claims['custom:tier']),
        memberships: membershipsFromClaim(claims['custom:memberships']),
        roles: rolesFromClaim(claims['cognito:groups']),
        ...(officeCode !== undefined ? { officeCode } : {}),
        ...(agentCode !== undefined ? { agentCode } : {}),
        grants: grantsFromClaim(claims['custom:grants']),
    });
}
//# sourceMappingURL=jwt.js.map